/* eslint-disable max-lines */

import React, { type FC, useState, useEffect, useRef } from "react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Label } from "./label";
import { Input } from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { ChevronUpIcon, ChevronDownIcon, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";

export interface NumericRangePickerProps {
  /** Click handler for applying the updates from NumericRangePicker. */
  onUpdate?: (values: { range: NumericRange }) => void;
  /** Initial value for minimum */
  initialMin?: number;
  /** Initial value for maximum */
  initialMax?: number;
  /** Alignment of popover */
  align?: "start" | "center" | "end";
  /** Label for the range (e.g., "Total", "Precio", "Cantidad") */
  label?: string;
  /** Currency symbol or prefix */
  prefix?: string;
  /** Suffix (e.g., "kg", "unidades") */
  suffix?: string;
  /** Allow decimal values */
  allowDecimals?: boolean;
  /** Number of decimal places */
  decimalPlaces?: number;
  /** Step for increments */
  step?: number;
  /** Custom presets */
  presets?: NumericPreset[];
  /** Clases extra del botón que abre el picker. */
  triggerClassName?: string;
}

export interface NumericRange {
  min: number | undefined;
  max: number | undefined;
}

interface NumericPreset {
  name: string;
  label: string;
  min: number;
  max: number;
}

// Presets por defecto para rangos de totales/precios
const DEFAULT_PRESETS: NumericPreset[] = [
  { name: "0-100", label: "L. 0 - L. 100", min: 0, max: 100 },
  { name: "100-500", label: "L. 100 - L. 500", min: 100, max: 500 },
  { name: "500-1000", label: "L. 500 - L. 1,000", min: 500, max: 1000 },
  { name: "1000-5000", label: "L. 1,000 - L. 5,000", min: 1000, max: 5000 },
  { name: "5000-10000", label: "L. 5,000 - L. 10,000", min: 5000, max: 10000 },
  { name: "10000+", label: "L. 10,000+", min: 10000, max: Infinity },
];

const formatNumber = (
  value: number | undefined,
  prefix?: string,
  suffix?: string,
  decimalPlaces?: number,
): string => {
  if (value === undefined) return "";

  const formatted =
    value === Infinity
      ? "∞"
      : value.toLocaleString("es-HN", {
          minimumFractionDigits: decimalPlaces || 0,
          maximumFractionDigits: decimalPlaces || 0,
        });

  return `${prefix || ""}${formatted}${suffix || ""}`;
};

/** The NumericRangePicker component allows a user to select a numeric range */
export const NumericRangePicker: FC<NumericRangePickerProps> & {
  filePath: string;
} = ({
  initialMin,
  initialMax,
  onUpdate,
  align = "end",
  label = "Total",
  prefix = "L. ",
  suffix = "",
  allowDecimals = true,
  decimalPlaces = 2,
  step = 1,
  presets = DEFAULT_PRESETS,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [range, setRange] = useState<NumericRange>({
    min: initialMin,
    max: initialMax,
  });

  // Sync internal state when props change (e.g., from URL/localStorage)
  useEffect(() => {
    setRange({ min: initialMin, max: initialMax });
  }, [initialMin, initialMax]);

  // Refs to store the values when the picker is opened
  const openedRangeRef = useRef<NumericRange | undefined>(undefined);

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(
    undefined,
  );

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setPreset = (presetName: string): void => {
    const preset = presets.find(({ name }) => name === presetName);
    if (!preset) return;

    setRange({
      min: preset.min,
      max: preset.max === Infinity ? undefined : preset.max,
    });
    setSelectedPreset(presetName);
  };

  const checkPreset = (): void => {
    for (const preset of presets) {
      const maxMatch =
        preset.max === Infinity
          ? range.max === undefined
          : preset.max === range.max;

      if (preset.min === range.min && maxMatch) {
        setSelectedPreset(preset.name);
        return;
      }
    }

    setSelectedPreset(undefined);
  };

  const resetValues = (): void => {
    setRange({
      min: initialMin,
      max: initialMax,
    });
    setSelectedPreset(undefined);
  };

  useEffect(() => {
    checkPreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  // Helper function to check if two ranges are equal
  const areRangesEqual = (a?: NumericRange, b?: NumericRange): boolean => {
    if (!a || !b) return a === b;
    return a.min === b.min && a.max === b.max;
  };

  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleMinChange = (value: string) => {
    if (value === "") {
      setRange({ ...range, min: undefined });
      return;
    }

    const numValue = allowDecimals ? parseFloat(value) : parseInt(value, 10);
    if (!isNaN(numValue)) {
      setRange({ ...range, min: numValue });
    }
  };

  const handleMaxChange = (value: string) => {
    if (value === "") {
      setRange({ ...range, max: undefined });
      return;
    }

    const numValue = allowDecimals ? parseFloat(value) : parseInt(value, 10);
    if (!isNaN(numValue)) {
      setRange({ ...range, max: numValue });
    }
  };

  // Validar que min no sea mayor que max
  const isValidRange = (): boolean => {
    if (range.min === undefined || range.max === undefined) return true;
    return range.min <= range.max;
  };

  const getDisplayText = (): string => {
    if (range.min === undefined && range.max === undefined) {
      return `Seleccionar ${label.toLowerCase()}`;
    }

    if (range.min !== undefined && range.max === undefined) {
      return `Desde ${formatNumber(range.min, prefix, suffix, decimalPlaces)}`;
    }

    if (range.min === undefined && range.max !== undefined) {
      return `Hasta ${formatNumber(range.max, prefix, suffix, decimalPlaces)}`;
    }

    return `${formatNumber(range.min, prefix, suffix, decimalPlaces)} - ${formatNumber(range.max, prefix, suffix, decimalPlaces)}`;
  };

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal hover:bg-none 2xl:min-w-55",
            range.min === undefined &&
              range.max === undefined &&
              "text-muted-foreground",
            triggerClassName,
          )}
        >
          <DollarSign className="opacity-50 2xl:mr-3" />
          <div className="hidden 2xl:flex flex-col gap-1 flex-1">
							<div className="text-xs font-medium truncate">
								{getDisplayText()}
							</div>
						</div>
          <div className="hidden 2xl:block ml-2 opacity-50">
							{isOpen ? (
								<ChevronUpIcon />
							) : (
								<ChevronDownIcon />
							)}
						</div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={isSmallScreen ? "center" : align}
        className="w-auto p-0"
      >
        <div className="flex flex-col">
          {/* Panel de controles */}
          <div className="flex flex-col p-3 space-y-3 min-w-70">
            {/* Header */}
            <div className="flex flex-col gap-0.5 pb-2 border-b">
              <h4 className="font-semibold text-xs">Seleccionar {label}</h4>
              <p className="text-[11px] text-muted-foreground">
                Elige un rango numérico
              </p>
            </div>

            {/* Inputs de rango */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Rango de valores
                </Label>
                <div className="flex flex-col gap-3">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">
                      Valor mínimo
                    </Label>
                    <div className="relative">
                      {prefix && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {prefix}
                        </span>
                      )}
                      <Input
                        type="number"
                        value={range.min ?? ""}
                        onChange={(e) => handleMinChange(e.target.value)}
                        step={step}
                        placeholder="Sin mínimo"
                        className={cn(prefix && "pl-10", suffix && "pr-16")}
                      />
                      {suffix && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {suffix}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">
                      Valor máximo
                    </Label>
                    <div className="relative">
                      {prefix && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {prefix}
                        </span>
                      )}
                      <Input
                        type="number"
                        value={range.max ?? ""}
                        onChange={(e) => handleMaxChange(e.target.value)}
                        step={step}
                        placeholder="Sin máximo"
                        className={cn(prefix && "pl-10", suffix && "pr-16")}
                      />
                      {suffix && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {suffix}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mensaje de error */}
                {!isValidRange() && (
                  <p className="text-xs text-red-500 mt-2">
                    El valor mínimo no puede ser mayor que el máximo
                  </p>
                )}
              </div>
            </div>

            {/* Select de accesos rápidos */}
            {presets.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Accesos rápidos
                </Label>
                <Select
                  value={selectedPreset}
                  onValueChange={(value) => {
                    setPreset(value);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar rango..." />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((preset) => (
                      <SelectItem key={preset.name} value={preset.name}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Botón para limpiar */}
            {(range.min !== undefined || range.max !== undefined) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRange({ min: undefined, max: undefined });
                  setSelectedPreset(undefined);
                }}
                className="text-xs"
              >
                Limpiar filtro
              </Button>
            )}
          </div>

          {/* Footer con botones de acción */}
          <div className="flex items-center justify-between gap-2 p-4 border-t bg-muted/30">
            <div className="text-xs text-muted-foreground flex-1">
              {selectedPreset && (
                <span className="font-medium">
                  {presets.find((p) => p.name === selectedPreset)?.label}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setIsOpen(false);
                  resetValues();
                }}
                variant="outline"
                size="sm"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (!isValidRange()) return;

                  setIsOpen(false);
                  if (!areRangesEqual(range, openedRangeRef.current)) {
                    onUpdate?.({ range });
                  }
                }}
                size="sm"
                disabled={!isValidRange()}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

NumericRangePicker.filePath = "components/ui/numeric-range-picker.tsx";
